import * as Api from '@ViewModels';
import type { Edge, Node } from '@xyflow/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCommitAutomationDraftMutation } from '../../../../queries/Automation';
import { useAutomationTemplatesByStatusQuery } from '../../../../queries/AutomationTemplate/useAutomationTemplatesByStatus';
import type { BaseNodeData } from '../types';
import {
	validateAutomationTemplate,
	type StaleScheduleRepair,
	type ValidationError,
	type ValidationResult,
} from '../utils/validateAutomation';

export interface StaleScheduleErrorWithRepair extends ValidationError {
	repair: StaleScheduleRepair;
}

/**
 * Configuration options for the usePublishAutomation hook.
 */
interface UsePublishAutomationOptions {
	/** Automation template to validate and publish */
	template: Api.IAutomationTemplate | undefined;
	/**
	 * Expanded flow graph used for validations that require branch/segment inspection.
	 *
	 * IMPORTANT: When a draft exists, this should represent the *draft* flow (not the published preview),
	 * since publishing always commits the draft.
	 */
	flow?: {
		nodes: Node<BaseNodeData>[];
		edges: Edge[];
		/** True when nodes/edges represent the fully expanded flow and are safe to validate against. */
		isReady: boolean;
	};
	/** Whether to automatically validate when template changes (default: true) */
	autoValidate?: boolean;
	/** Callback invoked after successful publish */
	onPublishSuccess?: () => void;
	/** Callback invoked when publish fails */
	onPublishError?: (error: Api.IOperationResultNoValue) => void;
	/** Callback invoked when validation finds errors */
	onValidationError?: (result: ValidationResult) => void;
	/** Optional impersonation context for admin operations */
	impersonationContext?: Api.IImpersonationContext;
}

/**
 * Return value from usePublishAutomation providing validation and publish controls.
 */
interface UsePublishAutomationReturn {
	/** Function to validate and publish the automation draft */
	publish: (options?: { ignorePromptValidation?: boolean }) => Promise<void>;
	/** Function to manually trigger validation without publishing */
	validate: () => ValidationResult;
	/** Whether a publish operation is currently in progress */
	isPublishing: boolean;
	/** Most recent validation result, null if never validated */
	validationResult: ValidationResult | null;
	/** Whether the automation passes validation and can be published */
	canPublish: boolean;
	/** Array of step IDs that have validation errors */
	invalidStepIds: string[];
	/** ID of the first invalid step (useful for scrolling to errors) */
	firstInvalidStepId: string | null;
	/** Whether the validation result contains any errors */
	hasErrors: boolean;
	/** Whether the validation result contains any warnings */
	hasWarnings: boolean;
	/**
	 * Validation errors that carry a repair payload — switch-branch child steps whose
	 * persisted schedule.criteria no longer matches the root trigger's anchoring rule.
	 * Consumed by the one-click refresh banner so it can PUT the corrected schedules
	 * without re-walking the flow graph.
	 */
	staleScheduleRepairs: StaleScheduleErrorWithRepair[];
}

/**
 * Provides validation and publishing functionality for automation templates.
 *
 * This hook manages the complete publish workflow including pre-publish validation,
 * error handling, and navigation after successful publish. Publishing commits the
 * draft version of an automation, making it active and visible to contacts.
 *
 * Key behaviors:
 * - Automatically validates template when it changes (if autoValidate is true)
 * - Blocks publish if validation fails (canPublish flag)
 * - Navigates to /automations after successful publish
 * - Provides granular validation results for displaying errors in the UI
 * - Tracks which specific steps have errors for highlighting
 *
 * Validation checks:
 * - Template has at least one step
 * - All steps have required content (email body, text message, etc.)
 * - Email steps have valid sender configuration
 * - Switch nodes have at least one case plus default
 * - All conditional branches are properly configured
 * - No orphaned steps (steps with no incoming edges)
 *
 * Publish vs Save:
 * - Save: Updates draft version, no validation required
 * - Publish: Commits draft to published, requires validation to pass
 * - Published versions are what contacts see when automation runs
 * - Draft changes don't affect running automations until published
 *
 * @param options - Configuration object
 * @param options.template - Automation template to validate/publish
 * @param options.autoValidate - Auto-validate on template changes (default: true)
 * @param options.onPublishSuccess - Callback on successful publish
 * @param options.onPublishError - Callback on publish error
 * @param options.onValidationError - Callback on validation failure
 * @returns Object with validation state and publish controls
 *
 * @example
 * ```typescript
 * const {
 *   publish,
 *   validate,
 *   canPublish,
 *   isPublishing,
 *   validationResult,
 *   invalidStepIds
 * } = usePublishAutomation({
 *   template: automation,
 *   onPublishSuccess: () => {
 *     showToast('Automation published successfully!');
 *   },
 *   onValidationError: (result) => {
 *     showErrorToast(`Found ${result.errors.length} errors`);
 *   }
 * });
 *
 * // In publish button:
 * <Button
 *   onClick={publish}
 *   disabled={!canPublish || isPublishing}
 * >
 *   {isPublishing ? 'Publishing...' : 'Publish'}
 * </Button>
 *
 * // Highlight invalid steps:
 * const isInvalid = invalidStepIds.includes(stepId);
 * ```
 */
export function usePublishAutomation({
	template,
	flow,
	autoValidate = true,
	onPublishSuccess,
	onPublishError,
	onValidationError,
	impersonationContext,
}: UsePublishAutomationOptions): UsePublishAutomationReturn {
	const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);

	const { data: publishedTemplates, isLoading: isLoadingPublishedTemplates } = useAutomationTemplatesByStatusQuery({
		status: Api.TemplateStatus.Published,
		impersonationContext,
	});

	const commitDraftMutation = useCommitAutomationDraftMutation({
		onSuccess: () => {
			onPublishSuccess?.();
		},
		onError: error => {
			onPublishError?.(error);
		},
	});

	const validate = useCallback(() => {
		if (!template) {
			const emptyResult: ValidationResult = {
				isValid: false,
				errors: [
					{
						type: 'general',
						message: 'No template to validate',
						severity: 'error',
					},
				],
				warnings: [],
			};
			setValidationResult(emptyResult);
			return emptyResult;
		}

		const result = validateAutomationTemplate(template, {
			...(flow?.isReady
				? {
						flow: {
							nodes: flow.nodes,
							edges: flow.edges,
						},
					}
				: {}),
			targetAutomationTemplates: publishedTemplates,
		});
		setValidationResult(result);

		if (!result.isValid) {
			onValidationError?.(result);
		}

		return result;
	}, [template, flow, publishedTemplates, onValidationError]);

	// Auto-validate when template changes
	useEffect(() => {
		if (autoValidate && template) {
			validate();
		}
	}, [autoValidate, template, validate]);

	const publish = useCallback(
		async ({ ignorePromptValidation }: { ignorePromptValidation?: boolean } = {}) => {
			if (!template) {
				return;
			}

			// If we were given flow-based validation inputs, require them to be ready.
			if (flow && !flow.isReady) {
				return;
			}

			// Block publish until the published-templates query resolves; otherwise
			// target-eligibility validation silently no-ops and we can publish a
			// TriggerAutomationStep pointing at an ineligible target.
			if (isLoadingPublishedTemplates) {
				return;
			}

			// Always re-validate at publish time with the freshest published-templates list,
			// not the auto-validated result captured before the query resolved.
			const validation = validate();

			if (!validation.isValid) {
				return;
			}

			await commitDraftMutation.mutateAsync({
				id: template.id,
				impersonationContext,
				ignorePromptValidation,
			});
		},
		[template, flow, isLoadingPublishedTemplates, validate, commitDraftMutation, impersonationContext]
	);

	const invalidStepIds = useMemo(() => {
		if (!validationResult) {
			return [];
		}
		return validationResult.errors.filter(error => error.stepId).map(error => error.stepId!);
	}, [validationResult]);

	const firstInvalidStepId = useMemo(() => {
		return invalidStepIds.length > 0 ? invalidStepIds[0] : null;
	}, [invalidStepIds]);

	const hasErrors = useMemo(() => {
		return validationResult ? validationResult.errors.length > 0 : false;
	}, [validationResult]);

	const hasWarnings = useMemo(() => {
		return validationResult ? validationResult.warnings.length > 0 : false;
	}, [validationResult]);

	const staleScheduleRepairs = useMemo<StaleScheduleErrorWithRepair[]>(() => {
		if (!validationResult) {
			return [];
		}
		return validationResult.errors.filter(
			(error): error is StaleScheduleErrorWithRepair => error.code === 'STALE_SCHEDULE_CRITERIA' && error.repair != null
		);
	}, [validationResult]);

	const canPublish = Boolean(
		template?.draft && validationResult?.isValid && (!flow || flow.isReady) && !isLoadingPublishedTemplates
	);

	return {
		publish,
		validate,
		isPublishing: commitDraftMutation.isPending,
		validationResult,
		canPublish,
		invalidStepIds,
		firstInvalidStepId,
		hasErrors,
		hasWarnings,
		staleScheduleRepairs,
	};
}
